import React, { useCallback, useMemo, useState } from 'react'

import { useEditorState } from '@tiptap/react'
import { BubbleMenu as BaseBubbleMenu } from '@tiptap/react/menus'

import { LinkEditorPanel } from '@/components/common/TextArea/RichTextArea/menus/components/LinkEditorPanel'
import { LinkPreviewPanel } from '@/components/common/TextArea/RichTextArea/menus/components/LinkPreviewPanel'
import { Surface } from '@/components/common/TextArea/RichTextArea/menus/components/Surface'
import type { MenuProps } from '@/components/common/TextArea/RichTextArea/menus/types'

type BubbleMenuOptions = NonNullable<
  React.ComponentProps<typeof BaseBubbleMenu>['options']
>

export const LinkMenu: React.FC<MenuProps> = ({ editor, appendTo }) => {
  const [showEdit, setShowEdit] = useState<boolean>(false)

  const { link, target } = useEditorState({
    editor,
    selector: (ctx) => {
      const attrs = ctx.editor.getAttributes('link')
      return { link: attrs.href, target: attrs.target }
    },
  })

  const shouldShow = useCallback(() => {
    const isActive = editor.isActive('link')
    return isActive
  }, [editor])

  const handleHide = useCallback(() => {
    setShowEdit(false)
  }, [])

  const handleEdit = useCallback(() => {
    setShowEdit(true)
  }, [])

  const onSetLink = useCallback(
    (url: string, openInNewTab?: boolean) => {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: url, target: openInNewTab ? '_blank' : '' })
        .run()
      setShowEdit(false)
    },
    [editor],
  )

  const onUnsetLink = useCallback(() => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run()
    setShowEdit(false)
    return null
  }, [editor])

  const bubbleMenuOptions = useMemo<BubbleMenuOptions>(
    () => ({
      placement: 'bottom' as const,
      onHide: handleHide,
    }),
    [handleHide],
  )

  return (
    <BaseBubbleMenu
      editor={editor}
      pluginKey="linkMenu"
      shouldShow={shouldShow}
      updateDelay={0}
      options={bubbleMenuOptions}
    >
      <Surface className="p-1 shadow-md">
        {showEdit ? (
          <LinkEditorPanel
            initialUrl={link}
            initialOpenInNewTab={target === '_blank'}
            onSetLink={onSetLink}
            className="p-1"
          />
        ) : (
          <LinkPreviewPanel
            url={link}
            onClear={onUnsetLink}
            onEdit={handleEdit}
            className="pl-1"
          />
        )}
      </Surface>
    </BaseBubbleMenu>
  )
}

export default LinkMenu
